import { Children, type ReactNode, useEffect, useRef } from 'react';

import Loader from '@superfunnel/ui-core/Loader';
import { classNames } from '@superfunnel/ui-core/utils';

import { MasonryGrid } from '../Constraints/MasonryGrid';

interface GalleryGridProps {
  scrollElement: HTMLDivElement | null;
  isLoading: boolean;
  errorMessage?: string;
  emptyMessage: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  estimatedItemHeight: number;
  maxLanes: number;
  children: ReactNode;
}

export const GalleryGrid = ({
  scrollElement,
  isLoading,
  errorMessage,
  emptyMessage,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  estimatedItemHeight,
  maxLanes,
  children,
}: GalleryGridProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const itemCount = Children.count(children);

  useEffect(() => {
    const element = sentinelRef.current;
    if (!element || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) onLoadMore();
      },
      { root: scrollElement, rootMargin: '1000px 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore, itemCount, scrollElement]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-center text-red-500">{errorMessage}</p>
      </div>
    );
  }

  if (itemCount === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-sm text-base-600">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="relative p-1">
      <MasonryGrid
        scrollElement={scrollElement}
        estimatedItemHeight={estimatedItemHeight}
        overscanItems={12}
        maxLanes={maxLanes}
      >
        {children}
      </MasonryGrid>

      <div
        ref={sentinelRef}
        className={classNames(
          'w-full',
          hasNextPage && 'flex h-16 items-center justify-center',
        )}
      >
        {hasNextPage && <Loader />}
      </div>
    </div>
  );
};
